import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Truck, Clock, DollarSign, MapPin, AlertCircle, Plus, FileText } from 'lucide-react';
import { formatNyDateTime } from '../../utils/datetime';

const ActiveRentalCard = ({ rental, truck, onAddUpdate, onViewHistory, onRequestCheckout, language = 'pt-BR', compact = false }) => {
  const [timeElapsed, setTimeElapsed] = useState('');
  const [valorAcumulado, setValorAcumulado] = useState(0);

  useEffect(() => {
    const updateTime = () => {
      if (!rental?.check_in?.data) return;
      
      const inicio = new Date(rental.check_in.data);
      const agora = new Date();
      const diff = agora - inicio;
      
      // Sempre começar do dia 1 (check-in = dia 1)
      const dias = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
      const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      setTimeElapsed(`${dias} ${language === 'pt-BR' ? 'dias' : 'days'} ${horas}h`);
      
      // Calcular valor acumulado (dia 1 desde o início)
      const valorDiario = rental.financeiro?.valor_diario || 100;
      const valorAluguel = dias * valorDiario;
      const custosAdicionais = rental.total_custos_adicionais || 0;
      setValorAcumulado(valorAluguel + custosAdicionais);
    };
    
    updateTime();
    const interval = setInterval(updateTime, 60000);
    
    return () => clearInterval(interval);
  }, [rental, language]);

  if (!rental || !truck) return null;

  const lastUpdate = rental.updates && rental.updates.length > 0 
    ? rental.updates[rental.updates.length - 1] 
    : null;

  // Versão COMPACTA para Home
  if (compact) {
    return (
      <Card className="border-l-4 border-l-green-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Truck className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-semibold">{truck.modelo}</p>
                <p className="text-sm text-gray-600">{truck.placa} • {truck.cor}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-blue-600">{timeElapsed}</p>
              <p className="text-lg font-bold text-green-600">${valorAcumulado.toFixed(0)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {lastUpdate && (
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-xs text-gray-500">{language === 'pt-BR' ? 'Última atualização:' : 'Last update:'}</p>
                  <p className="text-sm font-medium truncate">{lastUpdate.titulo}</p>
                </div>
              )}
              {!lastUpdate && (
                <p className="text-xs text-gray-400 italic">{language === 'pt-BR' ? 'Nenhuma atualização' : 'No updates'}</p>
              )}
            </div>
            <div className="flex gap-2 ml-3">
              {onRequestCheckout && (
                <Button onClick={onRequestCheckout} size="sm" className="bg-green-600 hover:bg-green-700">
                  🏁 {language === 'pt-BR' ? 'Checkout' : 'Checkout'}
                </Button>
              )}
              <Button onClick={onAddUpdate} size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">{language === 'pt-BR' ? 'Add' : 'Add'}</span>
              </Button>
              <Button onClick={onViewHistory} size="sm" variant="outline">
                <FileText className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Versão COMPLETA para aba Trucks
  return (
    <Card className="border-l-4 border-l-green-500">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Truck className="h-5 w-5 text-green-600" />
          {language === 'pt-BR' ? 'CAMINHÃO EM USO' : 'TRUCK IN USE'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="font-semibold text-lg">{truck.modelo} ({truck.placa})</p>
          <p className="text-sm text-gray-600">{truck.cor} • {truck.ano}</p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-blue-50 p-2 rounded">
            <div className="flex items-center gap-1 text-blue-700 mb-1">
              <Clock className="h-4 w-4" />
              <span className="font-semibold">{language === 'pt-BR' ? 'Tempo' : 'Time'}</span>
            </div>
            <p className="font-mono">{timeElapsed}</p>
            <p className="text-xs text-gray-600">{language === 'pt-BR' ? 'Desde' : 'Since'}: {new Date(rental.check_in.data).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}</p>
          </div>

          <div className="bg-green-50 p-2 rounded">
            <div className="flex items-center gap-1 text-green-700 mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="font-semibold">{language === 'pt-BR' ? 'Valor' : 'Amount'}</span>
            </div>
            <p className="font-mono font-bold">${valorAcumulado.toFixed(2)}</p>
            <p className="text-xs text-gray-600">{language === 'pt-BR' ? 'Acumulado' : 'Accumulated'}</p>
          </div>
        </div>

        <div className="text-sm space-y-1">
          <p className="flex items-center gap-2">
            <span className="font-semibold">{language === 'pt-BR' ? 'KM Inicial:' : 'Initial KM:'}</span>
            {rental.check_in.km_inicial?.toLocaleString()} km
          </p>
          <p className="flex items-center gap-2">
            <span className="font-semibold">{language === 'pt-BR' ? 'Combustível:' : 'Fuel:'}</span>
            {rental.check_in.combustivel_inicial}
          </p>
        </div>

        <div className="bg-gray-50 p-3 rounded">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-sm flex items-center gap-1">
              <FileText className="h-4 w-4" />
              {language === 'pt-BR' ? 'ATUALIZAÇÕES' : 'UPDATES'}
            </p>
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              {rental.total_atualizacoes || 0}
            </span>
          </div>
          
          {lastUpdate ? (
            <div className="text-sm bg-white p-2 rounded border">
              <p className="font-semibold text-xs text-gray-600">
                {language === 'pt-BR' ? 'Última' : 'Last'}: {formatNyDateTime(lastUpdate.data_hora)}
              </p>
              <p className="mt-1">{lastUpdate.titulo}</p>
              {lastUpdate.fotos && lastUpdate.fotos.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">📷 {lastUpdate.fotos.length} {language === 'pt-BR' ? 'fotos' : 'photos'}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">{language === 'pt-BR' ? 'Nenhuma atualização ainda' : 'No updates yet'}</p>
          )}
        </div>

        <div className="flex gap-2">
          {onRequestCheckout && (
            <Button onClick={onRequestCheckout} size="sm" className="flex-1 bg-green-600 hover:bg-green-700">
              🏁 {language === 'pt-BR' ? 'Checkout' : 'Checkout'}
            </Button>
          )}
          <Button onClick={onAddUpdate} size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1" />
            {language === 'pt-BR' ? 'Adicionar' : 'Add'}
          </Button>
          <Button onClick={onViewHistory} size="sm" variant="outline" className="flex-1">
            <FileText className="h-4 w-4 mr-1" />
            {language === 'pt-BR' ? 'Histórico' : 'History'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActiveRentalCard;